import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Grid, Button } from "@/comn/components";
import { useForm, useFetch, useToast, useModal, useResource, usePage, useGrid } from "@/comn/hooks";
import {
    URLS,
    APIS,
    SF_VHCL_MKER_MGMT_INFO,
    SG_VHCL_MDL,
    SG_VHCL_MDL_NO,
    SF_VHCL_MDL_INFO,
    SF_VHCL_MDL_NO_INFO,
} from "./services/OsciVhclMkerMgmtService";
import { OSCI0105007S } from "./OSCI0105007S";
import { OSCI0105008S } from "./OSCI0105008S";

/*
 * Vehicle Maker Management Detail
 * !== 차량 제조사 관리 상세 조회 ==!
 */
export const OSCI0105006Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { vhclMkerCd } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    const [vhclMdlCd, setVhclMdlCd] = useState<String>("");

    const [vhclMdlLst, setVhclMdlLst] = useState(comnUtils.getGridData([]));
    const [vhclMdlNoLst, setVhclMdlNoLst] = useState(comnUtils.getGridData([]));

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        vhclMkerMgmtInfo: useForm({
            defaultSchema: SF_VHCL_MKER_MGMT_INFO,
        }),
        vhclMdlInfo: useForm({
            defaultSchema: SF_VHCL_MDL_INFO,
        }),
        vhclMdlNoInfo: useForm({
            defaultSchema: SF_VHCL_MDL_NO_INFO,
        }),
    };

    const grid = {
        // Vehicle Maker Management List !== HS 관리 목록 ==!
        osciVhclMdl: useGrid({
            defaultSchema: SG_VHCL_MDL,
        }),
        // Vehicle Maker Management List !== HS 관리 목록 ==!
        osciVhclMdlNo: useGrid({
            defaultSchema: SG_VHCL_MDL_NO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Vehicle Maker Information !== 차량 제조사 정보 조회 ==!
        getVhclMkerInfo: useFetch({
            api: (data) => APIS.getVhclMkerInfo(vhclMkerCd),
            enabled: !!vhclMkerCd,
            onSuccess: (data) => {
                const vhclMkerMgmtInfo = data.vhclMkerMgmtInfo.content;
                form.vhclMkerMgmtInfo.setValues(vhclMkerMgmtInfo);

                fetch.getVhclMdlList.fetch(vhclMkerCd);
            },
            onError: (error) => {},
            showToast: true,
        }),

        getVhclMdlList: useFetch({
            api: (data) => APIS.getVhclMdlList(vhclMkerCd),
            onSuccess: (data) => {
                setVhclMdlLst(data.vhclMdlList);
                setVhclMdlCd("");
            },
            onError: (error) => {},
            showToast: true,
        }),

        getVhclMdlNoList: useFetch({
            api: (data) => APIS.getVhclMdlNoList(vhclMkerCd, vhclMdlCd),
            onSuccess: (data) => {
                setVhclMdlNoLst(data.vhclMdlNoList);
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteVhclMkerInfo: useFetch({
            api: (vhclMkerCd) => APIS.deleteVhclMkerInfo(vhclMkerCd),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onCancel: () => {
                        navigate(`${URLS.osci0105003q}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        deleteVhclMdlInfo: useFetch({
            api: (vhclMdlCds: any) => APIS.deleteVhclMdlInfo(vhclMkerCd, vhclMdlCds),
            onSuccess: () => {
                modal.openModal({ content: "msg.osc.00071" }); // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                setVhclMdlNoLst(comnUtils.getGridData([]));
                fetch.getVhclMdlList.fetch(vhclMkerCd);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00040" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),

        deleteVhclMdlNoInfo: useFetch({
            api: (vhclMdlNoCds: any) => APIS.deleteVhclMdlNoInfo(vhclMkerCd, vhclMdlCd, vhclMdlNoCds),
            onSuccess: () => {
                modal.openModal({ content: "msg.osc.00071" }); // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                fetch.getVhclMdlNoList.fetch(vhclMkerCd);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00040" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Vehicle Maker Information !== 차량 제조사 정보 삭제 ==!
        deleteVhclMkerInfo: () => {
            modal.openModal({
                content: "msg.osc.00095",
                onConfirm: () => {
                    fetch.deleteVhclMkerInfo.fetch(vhclMkerCd);
                },
            });
        },

        onGridRowClick: (data: any) => {
            setVhclMdlCd(data.vhclMdlCd);
            setTimeout(() => {
                fetch.getVhclMdlNoList.fetch(data.vhclMdlCd);
            }, 500);
        },

        onMdlModalCallback: (data: any) => {
            setVhclMdlNoLst(comnUtils.getGridData([]));
            fetch.getVhclMdlList.fetch(vhclMkerCd);
            modal.closeModal();
        },

        onBtnMdlGrdAddRowClick: () => {
            modal.openModal({
                draggable: true,
                size: "sm",
                content: <OSCI0105007S vhclMkerCd={vhclMkerCd} callback={handler.onMdlModalCallback} />,
            });
        },

        onBtnMdlGrdDelRowClick: () => {
            const list: any[] = grid.osciVhclMdl.getChecked() || [];
            if (comnUtils.isEmpty(list)) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    const vhclMdlCds: string[] = [];
                    list.forEach((item) => {
                        vhclMdlCds.push(`${item.vhclMdlCd}`);
                    });
                    fetch.deleteVhclMdlInfo.fetch(vhclMdlCds.join(","));
                },
            });
        },

        onMdlModalNoCallback: (data: any) => {
            fetch.getVhclMdlNoList.fetch();
            modal.closeModal();
        },
        onBtnMdlNoGrdAddRowClick: () => {
            if (comnUtils.isEmpty(vhclMdlCd)) {
                toast.showToast({ type: "warning", content: t("msg.osc.00053", { 0: t("L_VHCL_MDL_CD") }) });
                return null;
            }

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <OSCI0105008S
                        vhclMkerCd={vhclMkerCd}
                        vhclMdlCd={vhclMdlCd}
                        callback={handler.onMdlModalNoCallback}
                    />
                ),
            });
        },

        onBtnMdlNoGrdDelRowClick: () => {
            const list: any[] = grid.osciVhclMdlNo.getChecked() || [];
            if (comnUtils.isEmpty(list)) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    const vhclMdlNoCds: string[] = [];
                    list.forEach((item) => {
                        vhclMdlNoCds.push(`${item.vhclMdlNoCd}`);
                    });
                    fetch.deleteVhclMdlNoInfo.fetch(vhclMdlNoCds.join(","));
                },
            });
        },
    };

    const render = {
        osciVhclMdl: {
            head: {},
            cell: {
                vhclMdlMdfc: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Button
                            type="button"
                            color="success"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <OSCI0105007S
                                            vhclMkerCd={vhclMkerCd}
                                            vhclMdlCd={rowValues.vhclMdlCd}
                                            callback={handler.onMdlModalCallback}
                                        />
                                    ),
                                });
                            }}
                        >
                            {t("B_EDIT")}
                        </Button>
                    );
                },
            },
            edit: {},
        },
        osciVhclMdlNo: {
            head: {},
            cell: {
                vhclMdlNoMdfc: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Button
                            type="button"
                            color="success"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <OSCI0105008S
                                            vhclMkerCd={vhclMkerCd}
                                            vhclMdlCd={vhclMdlCd}
                                            vhclMdlNoCd={rowValues.vhclMdlNoCd}
                                            callback={handler.onMdlModalNoCallback}
                                        />
                                    ),
                                });
                            }}
                        >
                            {t("B_EDIT")}
                        </Button>
                    );
                },
            },
            edit: {},
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        form.vhclMkerMgmtInfo.setEditable(false);
        form.vhclMkerMgmtInfo.setSchema("loclYn", { viewType: "label" });
        form.vhclMkerMgmtInfo.setSchema("useYn", { viewType: "label" });
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.vhclMkerMgmtInfo.schema.vhclMkerCd} />
                            <Group.Control {...form.vhclMkerMgmtInfo.schema.vhclMkerNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.vhclMkerMgmtInfo.schema.loclYn} />
                            <Group.Control {...form.vhclMkerMgmtInfo.schema.useYn} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_VHCL_MDL_CD_LST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnMdlGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnMdlGrdDelRowClick} />
                        </Layout.Right>

                        <Grid
                            {...grid.osciVhclMdl.grid}
                            data={vhclMdlLst}
                            render={render.osciVhclMdl}
                            onRowClick={handler.onGridRowClick}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_VHCL_MDL_NO_CD_LST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnMdlNoGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnMdlNoGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.osciVhclMdlNo.grid} data={vhclMdlNoLst} render={render.osciVhclMdlNo} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button
                        role={"list"}
                        onClick={() => {
                            navigate(`${URLS.osci0105003q}`);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button
                        role={"edit"}
                        onClick={() => {
                            navigate(`${URLS.osci0105005s}/${vhclMkerCd}`);
                        }}
                    >
                        {t("B_EDIT")}
                    </Layout.Button>
                    <Layout.Button
                        role={"delete"}
                        onClick={() => {
                            handler.deleteVhclMkerInfo();
                        }}
                    >
                        {t("B_DEL")}
                    </Layout.Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
